import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Container, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt";
import ApiService from "../util/ApiService";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

require("./assets/css/Video.css");

const VideoStatsPage = () => {
    document.title = "Estatísticas de Vídeo | Plantão Rápido";

    const [loading, setLoading] = useState(false);
    const [dashboardStats, setDashboardStats] = useState(null);
    const [departmentStats, setDepartmentStats] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    useEffect(() => {
        fetchDashboardStats();
        fetchDepartments();
    }, []);

    useEffect(() => {
        if (selectedDepartment?.value) {
            fetchDepartmentStats(selectedDepartment.value);
        }
    }, [selectedDepartment]);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);

            const params = {};
            if (dateRange[0]) {
                params.date_from = dateRange[0].toISOString().split('T')[0];
            }
            if (dateRange[1]) {
                params.date_to = dateRange[1].toISOString().split('T')[0];
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/stats/dashboard`,
                params,
                true
            );

            if (response.code === 200 && response.data) {
                setDashboardStats(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        }
    };

    const fetchDepartmentStats = async (departmentId) => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/stats/department/${departmentId}`,
                {},
                true
            );

            if (response.code === 200 && response.data) {
                setDepartmentStats([response.data]);
            }
        } catch (error) {
            console.error('Erro ao carregar estatísticas do departamento:', error);
        }
    };

    const formatTime = (minutes) => {
        if (!minutes) return '0min';
        const hrs = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);

        if (hrs > 0) {
            return `${hrs}h ${mins}min`;
        }
        return `${mins}min`;
    };

    const StatCard = ({ title, value, icon, color = 'primary', subtitle }) => (
        <Card className="card-animate">
            <CardBody>
                <div className="d-flex justify-content-between">
                    <div>
                        <p className="fw-medium text-muted mb-0">{title}</p>
                        <h2 className={`mt-4 ff-secondary fw-semibold text-${color}`}>
                            {loading ? <Skeleton width={60} /> : value}
                        </h2>
                        {subtitle && (
                            <p className="mb-0 text-muted">
                                <small>{subtitle}</small>
                            </p>
                        )}
                    </div>
                    <div>
                        <div className={`avatar-sm flex-shrink-0`}>
                            <span className={`avatar-title bg-${color}-subtle rounded-circle fs-2`}>
                                <i className={`fa ${icon} text-${color}`}></i>
                            </span>
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Estatísticas" pageTitle="Vídeo Chamada" />

                    {/* Filters */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <div className="form-group">
                                <label>Departamento</label>
                                <Select
                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                    value={selectedDepartment}
                                    onChange={setSelectedDepartment}
                                    placeholder="Todos os departamentos"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="form-group">
                                <label>Período</label>
                                <Flatpickr
                                    className="form-control"
                                    value={dateRange}
                                    onChange={setDateRange}
                                    options={{
                                        mode: "range",
                                        dateFormat: "d/m/Y",
                                        locale: Portuguese
                                    }}
                                    placeholder="Selecione o período"
                                />
                            </div>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                            <button
                                className="btn btn-success"
                                onClick={fetchDashboardStats}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                ) : (
                                    <i className="fa fa-refresh me-2"></i>
                                )}
                                Atualizar
                            </button>
                        </Col>
                    </Row>

                    {/* Main Stats */}
                    <Row>
                        <Col md={3}>
                            <StatCard
                                title="Total de Atendimentos"
                                value={dashboardStats?.total_attendances || 0}
                                icon="fa-video-camera"
                                color="primary"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Finalizados"
                                value={dashboardStats?.completed || 0}
                                icon="fa-check-circle"
                                color="success"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Cancelados"
                                value={dashboardStats?.cancelled || 0}
                                icon="fa-times-circle"
                                color="danger"
                            />
                        </Col>
                        <Col md={3}>
                            <StatCard
                                title="Não Compareceram"
                                value={dashboardStats?.no_show || 0}
                                icon="fa-user-times"
                                color="warning"
                            />
                        </Col>
                    </Row>

                    {/* Time Stats */}
                    <Row className="mt-3">
                        <Col md={4}>
                            <StatCard
                                title="Tempo Médio de Espera"
                                value={formatTime(dashboardStats?.avg_wait_time)}
                                icon="fa-clock"
                                color="info"
                                subtitle="Tempo até iniciar atendimento"
                            />
                        </Col>
                        <Col md={4}>
                            <StatCard
                                title="Tempo Médio de Atendimento"
                                value={formatTime(dashboardStats?.avg_attendance_time)}
                                icon="fa-stopwatch"
                                color="secondary"
                                subtitle="Duração média das chamadas"
                            />
                        </Col>
                        <Col md={4}>
                            <StatCard
                                title="Taxa de Sucesso"
                                value={`${dashboardStats?.success_rate || 0}%`}
                                icon="fa-chart-line"
                                color="success"
                                subtitle="Atendimentos finalizados com sucesso"
                            />
                        </Col>
                    </Row>

                    {/* Today Stats */}
                    <Row className="mt-4">
                        <Col md={12}>
                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">
                                        <i className="fa fa-calendar-day me-2"></i>
                                        Estatísticas de Hoje
                                    </h5>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={3}>
                                            <div className="video-stats-card">
                                                <div className="stat-value text-warning">
                                                    {loading ? <Skeleton width={40} /> : (dashboardStats?.today?.in_queue || 0)}
                                                </div>
                                                <div className="stat-label">Na Fila Agora</div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="video-stats-card">
                                                <div className="stat-value text-success">
                                                    {loading ? <Skeleton width={40} /> : (dashboardStats?.today?.in_progress || 0)}
                                                </div>
                                                <div className="stat-label">Em Atendimento</div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="video-stats-card">
                                                <div className="stat-value text-info">
                                                    {loading ? <Skeleton width={40} /> : (dashboardStats?.today?.completed || 0)}
                                                </div>
                                                <div className="stat-label">Finalizados Hoje</div>
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="video-stats-card">
                                                <div className="stat-value">
                                                    {loading ? <Skeleton width={40} /> : (dashboardStats?.today?.total || 0)}
                                                </div>
                                                <div className="stat-label">Total Hoje</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Department Stats */}
                    {selectedDepartment && departmentStats.length > 0 && (
                        <Row className="mt-4">
                            <Col md={12}>
                                <Card>
                                    <CardHeader>
                                        <h5 className="card-title mb-0">
                                            <i className="fa fa-building me-2"></i>
                                            Estatísticas: {selectedDepartment.label}
                                        </h5>
                                    </CardHeader>
                                    <CardBody>
                                        <Row>
                                            {departmentStats.map((stat, index) => (
                                                <React.Fragment key={index}>
                                                    <Col md={3}>
                                                        <div className="video-stats-card">
                                                            <div className="stat-value">{stat.total || 0}</div>
                                                            <div className="stat-label">Total</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="video-stats-card">
                                                            <div className="stat-value text-success">{stat.completed || 0}</div>
                                                            <div className="stat-label">Finalizados</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="video-stats-card">
                                                            <div className="stat-value">{formatTime(stat.avg_wait_time)}</div>
                                                            <div className="stat-label">Tempo Médio Espera</div>
                                                        </div>
                                                    </Col>
                                                    <Col md={3}>
                                                        <div className="video-stats-card">
                                                            <div className="stat-value">{formatTime(stat.avg_attendance_time)}</div>
                                                            <div className="stat-label">Tempo Médio Atendimento</div>
                                                        </div>
                                                    </Col>
                                                </React.Fragment>
                                            ))}
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Empty State for no data */}
                    {!loading && !dashboardStats && (
                        <Row className="mt-4">
                            <Col md={12}>
                                <div className="video-empty-state">
                                    <i className="fa fa-chart-bar"></i>
                                    <h5 className="text-muted">Nenhuma estatística disponível</h5>
                                    <p className="text-muted">As estatísticas aparecerão quando houver atendimentos.</p>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoStatsPage;
